import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { cloneDeep } from 'lodash'
import { layoutStore, type PageLayoutStore } from 'shared/layout'
import { uiStore } from 'shared/store/uiStore'
import { RequestLoadData } from 'shared/api/lib'
import type {
  ICallHistory,
  IParamsGetCallHistory,
  IParamsGetCallHistoryStatistic,
  IResponseCallHistory,
} from 'entities/CallHistory/api/types'
import { CallHistory } from 'entities/CallHistory/model/CallHistory'
import { CallHistoryApi } from 'entities/CallHistory/api/callHistory'
import type { IReadMessage } from 'entities/Conversation/api/types'
import type { IResponseEventInboxCalling } from 'entities/Inbox/api/types'
import { ConversationRoutes } from 'entities/ExportRouters'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import type { CallModalStore } from 'widgets/CallModal'
import type { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import type { IOpenConversationDetails } from 'widgets/ContactsDetails'
import { FiltersStore } from 'pages/conversations/pages/calls/ui/CallHistoryDropdownFilter'
import { CallHistoryFiltersStore } from 'pages/conversations/pages/calls/ui/CallHistoryFilters'
import { CallHistoryTableStore } from 'pages/conversations/pages/calls/ui/CallHistoryTable'
import { ExportCallHistoryModalStore } from 'pages/conversations/pages/calls/ui/ExportCallHistoryModal'

export class CallHistoryStore {
  private _callHistoryFiltersStore
  private _callHistoryTableStore
  private _filtersStore
  private _exportCallHistoryModalStore

  // === Reactions ==== ///
  private _disposeReactionParamsWithoutPage: IReactionDisposer | null = null
  private _disposeReactionParamsGetItems: IReactionDisposer | null = null
  private _disposeReactionEventFilters: IReactionDisposer | null = null

  // === Reactions-end ==== ///
  private _page = 1
  private _limit = tableLimitStore.getLimit('call-history-table') ?? 50
  private _total = 0
  private _search: string | null = null
  private _loading = true
  private _loadingMore = false
  private _loadingSearch = false
  private _filter = []
  private _itemsMap: Map<number, CallHistory> = new Map()
  private _isInfinityList = false

  constructor(
    private _callModalStore: CallModalStore,
    private _contactsDetailsManageStore: ContactsDetailsManageStore,
    private _pageLayoutStore: PageLayoutStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._callHistoryFiltersStore = new CallHistoryFiltersStore(this.loadCallHistory)
    this._callHistoryTableStore = new CallHistoryTableStore(this)
    this._filtersStore = new FiltersStore(this._entities)
    this._exportCallHistoryModalStore = new ExportCallHistoryModalStore()
  }

  get callStore() {
    return this._entities.callStore
  }

  get limit() {
    return this._limit
  }

  get loading() {
    return this._loading
  }

  get page() {
    return this._page
  }

  get total() {
    return this._total
  }

  get loadingSearch() {
    return this._loadingSearch
  }

  get loadingMore() {
    return this._loadingMore
  }

  get search() {
    return this._search
  }

  get callHistoryFiltersStore() {
    return this._callHistoryFiltersStore
  }

  get callHistoryTableStore() {
    return this._callHistoryTableStore
  }

  get filtersStore() {
    return this._filtersStore
  }

  get exportCallHistoryModalStore() {
    return this._exportCallHistoryModalStore
  }

  openExportModal = () => {
    this._exportCallHistoryModalStore.onOpenExport(this.paramsWithoutPage)
  }

  initReactions = () => {
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
    this.reactionEventFilters()
  }

  disposeReactions = () => {
    this._disposeReactionParamsWithoutPage?.()
    this._disposeReactionParamsGetItems?.()
    this._disposeReactionEventFilters?.()
  }

  reactionParamsWithoutPage = () => {
    this._disposeReactionParamsWithoutPage?.()
    this._disposeReactionParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._loading = true
        this._page = 1
      }
    )
  }
  reactionParamsGetItems = () => {
    this._disposeReactionParamsGetItems?.()
    this._disposeReactionParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }
  reactionEventFilters = () => {
    this._disposeReactionEventFilters?.()
    this._disposeReactionEventFilters = reaction(() => this.eventFilters, this.sendLog)
  }

  setIsInfinityList = (value: boolean) => {
    this._isInfinityList = value
  }

  get onOpenContactsDetails(): IOpenConversationDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        this._contactsDetailsManageStore.onOpenConversationDetails(data, 'call history')
      },
    }
  }

  get callModalStore() {
    return this._callModalStore
  }

  get isLoading() {
    return this._loadingSearch || this._loading
  }

  get isLoadingStatistics() {
    return this._callHistoryFiltersStore.loading
  }

  get isEmptySearchResults() {
    return (
      (Boolean(this._search) ||
        Boolean(this._callHistoryFiltersStore.filtersList.length) ||
        this.group !== 'open') &&
      !this.items.length &&
      !this._loadingSearch &&
      !this.isLoadingStatistics
    )
  }

  get isEmptyState() {
    return (
      !this._loading &&
      !this.isLoadingStatistics &&
      !this._itemsMap.size &&
      !this._search &&
      !Boolean(this._callHistoryFiltersStore.filtersList.length)
    )
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get filtersList() {
    return this._callHistoryFiltersStore.filtersList
  }

  get group() {
    return this._filtersStore.filter?.key
  }

  get groupLabel() {
    return this._filtersStore.filter?.label
  }

  get paramsWithoutPage(): Omit<IParamsGetCallHistory, 'page'> {
    return {
      search: this._search || null,
      limit: this._limit,
      team_id: this._entities.inboxesStore.currentInboxTypeId,
      team_type: this._entities.inboxesStore.currentInboxType || 3,
      group: this.group === 'all' ? null : this.group,
      filtersList: this.filtersList,
      sorting: [this._callHistoryTableStore.getSortingParams()],
    }
  }

  get paramsGetItems(): IParamsGetCallHistory {
    return {
      page: this._page,
      ...this.paramsWithoutPage,
    }
  }

  get eventFilters() {
    return {
      outcome: this._callHistoryFiltersStore.activeFilter?.outcome,
      source: this._callHistoryFiltersStore.activeFilter?.source,
      conversation_type: this.group,
    }
  }

  setData = ({ data, meta }: IResponseCallHistory) => {
    this.setItems(data)
    this._total = meta.total
  }

  setItems = (items: ICallHistory[]) => {
    items.forEach((item) => this.setItem(item))
  }

  setItem = (item: ICallHistory) => {
    this._itemsMap.set(item.message.id, new CallHistory(item, this._entities))
  }

  updateItem = (item: CallHistory) => {
    this._itemsMap.set(item.message.id, item)
  }

  getItem = (id: number) => {
    return this._itemsMap.get(id)
  }

  updateInboxCalling = (data: IResponseEventInboxCalling) => {
    this.items.forEach((item) => {
      if (item.inbox.id === data.team_id) {
        item.updateInboxCalling(data.number)
      }
    })
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      if (!this._callHistoryFiltersStore.hasCalls) {
        this.clear()

        return
      }

      if (this._search && this._entities.organizationStore.isHipaaCompliant) {
        this._entities.organizationStore.handleTrackOrganizationActivity({
          event: 'phi.contact.searched',
          data: {
            search_query: this._search,
          },
        })
      }

      this._loading = true

      const { data } = await CallHistoryApi.getCallHistory(this.paramsGetItems, {
        cancelToken: cancelRequest.token,
      })

      if (!this._isInfinityList || (this.paramsGetItems.page && this.paramsGetItems.page === 1)) {
        runInAction(() => {
          this._itemsMap.clear()
        })
      }

      this.setData(data)

      runInAction(() => {
        this._loading = false
        this._loadingSearch = false
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._loading = false
        this._loadingMore = false
      })
    },
  })

  loadCallHistory = async () => {
    return this._requestLoadData.loadData()
  }

  handleSearch = (key: string) => {
    this._search = key
    this._loadingSearch = true
  }

  onPaginationModelChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
    tableLimitStore.setLimit('call-history-table', limit)
  }

  markAsReadAllList = () => {
    this.items.forEach((item) => item.makeAsRead())
  }

  setItemToBegin = (item: ICallHistory) => {
    const currentItems = cloneDeep(this.items)

    this._itemsMap.clear()
    this.setItem(item)
    this.setItems(currentItems)
  }

  setAdditionItem = (item: ICallHistory) => {
    if (!this._callHistoryFiltersStore.hasCalls) {
      const params: IParamsGetCallHistoryStatistic = {
        team_id: this._entities.inboxesStore.currentInboxTypeId,
        team_type: this._entities.inboxesStore.currentInboxType || 3,
        group: this._filtersStore.filter?.key === 'all' ? null : this._filtersStore.filter?.key,
      }

      this._callHistoryFiltersStore.loadData(params)

      return
    }

    if (this._callHistoryFiltersStore.isActiveAllCalls) {
      this.setItemToBegin(item)

      return
    }

    const { type, outcome, source } = item.call

    const activeFilter = this._callHistoryFiltersStore.activeFilter
    const matchesFilter = activeFilter?.source
      ? activeFilter.type === type && activeFilter.source === source
      : activeFilter?.type === type && activeFilter.outcome === outcome

    if (matchesFilter) {
      this.setItemToBegin(item)
    }
  }

  updateCallHistoryByMessageReceivedEvent = (data: ICallHistory) => {
    const { call, message } = data
    const { id, unread } = message

    if (this.getItem(id)) {
      this.setItem(data)
    } else {
      if (unread) {
        this._callHistoryFiltersStore.increaseCount(call)
      }

      this.setAdditionItem(data)
    }
  }

  updateCallHistoryByVoicemailEvent = (data: ICallHistory) => {
    const { call, message } = data
    const { id, unread } = message
    const existingItem = this.getItem(id)

    if (existingItem) {
      if (existingItem.message.unread && existingItem.call.type) {
        const item: IReadMessage = {
          id: existingItem.message.id,
          type: 'call',
          direction: existingItem.call.type,
          status: existingItem.call.outcome,
          source: existingItem.call.source,
        }

        this._callHistoryFiltersStore.decreaseCount([item])
      }

      this.setItem(data)
    } else {
      this.setAdditionItem(data)
    }

    if (unread) {
      this._callHistoryFiltersStore.increaseCount(call)
    }
  }

  sendLog = () => {
    const activeFilter = this._callHistoryFiltersStore.activeFilter
    const outcome = this._callHistoryFiltersStore.isActiveAllCalls
      ? 'all-calls'
      : activeFilter?.source
      ? `${activeFilter.type} - ${activeFilter.source}`
      : `${activeFilter?.type} - ${activeFilter?.outcome}`

    const payload = {
      event_id: 'call_history_filter_used',
      outcome,
      conversation_type: this.groupLabel,
    }

    this._entities.eventLogStore.logEvent('Call History Filter Used', payload, {
      groupId: this._entities.organizationStore.id,
    })
  }

  disposeLoadingState = () => {
    this._loading = false
  }

  clear = () => {
    this._itemsMap.clear()
    this._loading = true
    this._loadingMore = false
    this._search = null
    this._page = 1
    this._total = 0
  }

  dispose = () => {
    this.clear()
    this._requestLoadData.dispose()
    this._callHistoryFiltersStore.reset()
    this._filtersStore.reset()
    this._callHistoryTableStore.dispose()
    this._exportCallHistoryModalStore.dispose()
  }

  get isHasMore() {
    return this._total > this._itemsMap.size
  }

  handleLoadMore = () => {
    if (this._loadingMore) return

    this._loadingMore = true
    this._page += 1
  }

  onOpenConversation = (callHistory: CallHistory) => {
    if (layoutStore.isMobileView) {
      uiStore.changeRoute({
        path: `/${ConversationRoutes.root}/${ConversationRoutes.calls}/${callHistory.conversation.id}`,
      })
      this._pageLayoutStore.toEndContainer()
    } else {
      this.onOpenContactsDetails.open({
        contactId: callHistory.contact?.id,
        inboxId: callHistory.inbox.id,
        conversationId: callHistory.conversation.id,
      })
    }
  }
}
